import { useContext, useEffect, useState } from 'react';
import Modal from 'components/ui/Modal';
import Button from 'components/ui/Button';
import Icon from 'components/ui/Icon';
import { LocalizationContext } from 'context/localization.context';
import {
	getCartContact,
	updateCartContact,
	CartContactModel,
} from 'api/proshop';
import { toast } from 'react-toastify';
import { GenericError } from 'common/helpers';

interface Props {
	url: string;
	onToggle: () => void;
}

const ProshopRequestHeadersModal = ({ url, onToggle }: Props) => {
	const { t }: any = useContext(LocalizationContext);

	const [formData, setFormData] = useState<CartContactModel>({
		goodsReceptionContactName: '',
		goodsReceptionPhoneNumber: '',
		customerRequisition: '',
		projectName: '',
		projectNumber: '',
		comment: '',
	});
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setLoading(true);
		getCartContact(url)
			.then((data) => {
				if (data) setFormData(data);
			})
			.catch((err) => {
				toast(t('shared/toast/servererror'), { type: toast.TYPE.ERROR });
				console.error(err);
			})
			.finally(() => setLoading(false));
	}, [url]);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	//handles contact/request-header informations patch request
	const handleSave = async () => {
		setSaving(true);
		try {
			await updateCartContact(url, formData);
			toast(t('shared/toast/savesuccess'), { type: toast.TYPE.SUCCESS });
			onToggle();
		} catch (err: any) {
			if (err instanceof GenericError) {
				toast(err.message, { type: toast.TYPE.WARNING });
			} else {
				toast(t('shared/toast/servererror'), { type: toast.TYPE.ERROR });
			}
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			toggleModal={onToggle}
			heading={t('cartpage/proshop/requestheaders')}
			title="Proshop request headers"
			width="medium"
		>
			{loading ? (
				<div className="flex justify-center py-8">
					<Icon icon="loader" animate="spin" size={5} aria-hidden={true} />
				</div>
			) : (
				<div className="flex flex-col gap-5">
					<div>
						<label className="block mb-1" htmlFor="goodsReceptionContactName">
							{t('cartpage/proshop/goodsreceptioncontactname')}
						</label>
						<input
							className="w-full p-2 border border-greyDark rounded-lg"
							id="goodsReceptionContactName"
							name="goodsReceptionContactName"
							type="text"
							value={formData.goodsReceptionContactName}
							onChange={handleChange}
						/>
					</div>

					<div>
						<label className="block mb-1" htmlFor="goodsReceptionPhoneNumber">
							{t('cartpage/proshop/goodsreceptionphonenumber')}
						</label>
						<input
							className="w-full p-2 border border-greyDark rounded-lg"
							id="goodsReceptionPhoneNumber"
							name="goodsReceptionPhoneNumber"
							type="text"
							value={formData.goodsReceptionPhoneNumber}
							onChange={handleChange}
						/>
					</div>

					<div>
						<label className="block mb-1" htmlFor="customerRequisition">
							{t('cartpage/proshop/customerrequisition')}
						</label>
						<input
							className="w-full p-2 border border-greyDark rounded-lg"
							id="customerRequisition"
							name="customerRequisition"
							type="text"
							value={formData.customerRequisition}
							onChange={handleChange}
						/>
					</div>

					<div>
						<label className="block mb-1" htmlFor="projectName">
							{t('cartpage/proshop/projectname')}
						</label>
						<input
							className="w-full p-2 border border-greyDark rounded-lg"
							id="projectName"
							name="projectName"
							type="text"
							value={formData.projectName}
							onChange={handleChange}
						/>
					</div>

					<div>
						<label className="block mb-1" htmlFor="projectNumber">
							{t('cartpage/proshop/projectnumber')}
						</label>
						<input
							className="w-full p-2 border border-greyDark rounded-lg"
							id="projectNumber"
							name="projectNumber"
							type="text"
							value={formData.projectNumber}
							onChange={handleChange}
						/>
					</div>

					<div>
						<label className="block mb-1" htmlFor="comment">
							{t('cartpage/proshop/comment')}
						</label>
						<textarea
							className="w-full h-25 p-2 border border-greyDark rounded-lg"
							id="comment"
							name="comment"
							value={formData.comment}
							onChange={handleChange}
						/>
					</div>

					<div className="flex justify-end gap-4">
						<Button type="button" buttonColor="grey" onClick={onToggle}>
							{t('shared/modal/cancel')}
						</Button>
						<Button
							type="button"
							buttonColor="blue"
							onClick={handleSave}
							disabled={saving}
						>
							{saving ? (
								<Icon
									icon="loader"
									animate="spin"
									size={1}
									aria-hidden={true}
								/>
							) : (
								t('shared/modal/save')
							)}
						</Button>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default ProshopRequestHeadersModal;
